<template>
  <s-page-block>
    <s-page-header :heading="`Graded Response (${response.id}) for ${task.title}`"></s-page-header>
    <s-details>
      <template #summary>Response MetaData:</template>
      <pre>
        <code>
          {{ JSON.stringify(response, null, 2) }}
        </code>
      </pre>
    </s-details>
    <s-details>
      <template #summary>Student Response:</template>
      <pre>
        <code>
          {{ JSON.stringify(responseData, null, 2) }}
        </code>
      </pre>
    </s-details>
    <s-details>
      <template #summary>Grade Data:</template>
      <s-task-grade v-if="grade" :grade="grade" :weight="100" />
    </s-details>
    <s-details>
      <template #summary>Feedback Data:</template>
      <div v-for="(part, partName) in feedbackByPart">
        <s-latex v-if="partName != 'default'" :content="partName" />
        <ul>
          <li v-for="feedback in part">
            <s-latex :content="feedback.content" />
          </li>
        </ul>
      </div>
    </s-details>
    <form @submit.prevent="submitForm" class="card padded">
      <DataDrivenGradeEntry
        v-if="task.taskType === 'DataDrivenTask'"
        :form="newDataDrivenFeedbackForm"
      />
      <ManualGradeEntry v-else :form="newEditFeedbackForm" />

      <!-- Submit/Delete Button -->
      <div class="flex flex-row flex-wrap gap-2 py-1">
        <s-btn color="red" icon="delete" @click.prevent="confirmDelete">Remove Response</s-btn>
        <s-btn type="submit" class="btn-primary">Save Changes</s-btn>
      </div>
    </form>
  </s-page-block>
</template>
<script setup lang="ts">
import SPageBlock from '../../../design-system/SPageBlock.vue';
import SPageHeader from '../../../design-system/SPageHeader.vue';
import {Task, TaskFeedback, TaskResponse} from '../../../types/entities/tasks';
import SDetails from '../../../design-system/SDetails.vue';
import {useBreadcrumbs} from '../../../composables/useBreadcrumbs';
import {route} from 'ziggy-js';
import {Grade} from '../../../types/entities/base-grade';
import SLatex from '../../../components/SLatex.vue';
import STaskGrade from '../../../components/STaskGrade.vue';
import SBtn from '../../../design-system/SBtn.vue';
import {router} from '@inertiajs/vue3';
import {useManualGradeOverrideForm} from '../../../composables/useManualGradeOverrideForm';
import {computed, toRef, toRefs} from 'vue';
import ManualGradeEntry from '../../AssignmentMarking/Show/ManualGradeEntry.vue';
import DataDrivenGradeEntry from '../../AssignmentMarking/Show/DataDrivenGradeEntry.vue';
import {useDataDrivenOverrideForm} from '../../../composables/useDataDrivenOverrideForm';
import TaskCriterionGradeDto = App.DTOs.Entities.Grades.TaskCriterionGradeDto;
import Rubric = App.DTOs.Rubrics.Rubric;
import TaskGradeDto = App.DTOs.TaskGradeDto;

const props = defineProps<{
  task: Task;
  response: TaskResponse;
  responseData: any;
  feedbackByPart: Record<string, TaskFeedback[]>;
  grade: Grade;
  partGrades: TaskCriterionGradeDto[];
  responseGrade: TaskGradeDto | null;
  taskRubric: Rubric | null;
}>();

const taskWeight = computed(() => props.grade.pointsAvailable || 1);

const newEditFeedbackForm = useManualGradeOverrideForm({
  ...toRefs(props),
  taskWeight,
});

const feedbacks = computed(() =>
  Object.keys(props.feedbackByPart).reduce(
    (acc, part) => [...acc, ...props.feedbackByPart[part]],
    [] as TaskFeedback[]
  )
);

const newDataDrivenFeedbackForm = useDataDrivenOverrideForm(
  toRef(() => props.taskRubric!),
  feedbacks,
  toRef(() => props.partGrades)
);

const submitForm = () => {
  const form =
    props.task.taskType === 'DataDrivenTask' ? newDataDrivenFeedbackForm : newEditFeedbackForm;

  form.put(
    route('admin.tasks.responses.update', {task: props.task.id, response: props.response.id})
  );
};

function confirmDelete() {
  if (
    confirm(
      'You are about to remove this response from the task evaluation set. This cannot be undone. Are you sure you want to proceed?'
    )
  ) {
    router.delete(
      route('admin.tasks.responses.destroy', {task: props.task.id, response: props.response.id})
    );
  }
}

useBreadcrumbs([
  {label: 'Tasks', href: route('admin.tasks.index')},
  {label: props.task.title, href: route('admin.tasks.show', {task: props.task.id})},
]);
</script>
